<template>
    <div>
        <page-header :title="title" :items="items" />

        <div v-if="!editMode || (editMode && category.id)" class="row">
            <div class="col-xl-9 mb-3">
                <div class="card">
                    <div class="card-body">
                        <add-edit-form
                            v-model="category"
                            :edit-mode="editMode"
                            :validation="$v"
                            :server-errors="serverErrors"
                            @on-name-change="onNameChange"
                        />
                    </div>
                </div>
            </div>

            <div class="col-xl-3 mb-3">
                <div class="card">
                    <div class="card-body">
                        <div :class="{ 'mb-3': editMode }">
                            <a
                                v-if="editMode"
                                :href="previewUrl"
                                class="btn btn-primary btn-static waves-effect waves-light m-1"
                                target="_blank"
                                @click="onPreviewButtonClick"
                            >
                                <i class="far fa-eye" />
                                Preview
                            </a>
                            <button
                                class="btn btn-success btn-static waves-effect waves-light m-1"
                                @click="submitHandler"
                            >
                                <i class="fas fa-save" />
                                Save
                            </button>
                        </div>

                        <item-information
                            :key="informationKey"
                            :edit-mode="editMode"
                            :sections="sections"
                            :item="category"
                            :slug="category.slug"
                            :options="{ slugBasicUrl: 'article-categories' }"
                            :slug-error="slugError"
                            @meta-change="onMetaChange"
                            @on-slug-change="onSlugChange"
                            @on-delete-confirm="onDeleteConfirm"
                        />
                    </div>
                </div>
            </div>
        </div>
        <skeleton-loader v-else />
    </div>
</template>

<script>
import { mapActions } from 'vuex';
import { required } from 'vuelidate/lib/validators';
import { StatusCodes as HTTP } from 'http-status-codes';
import { frontendUrl } from '@/config';
import ItemInformation from '@/components/elements/ItemInformation';
import AddEditForm from '@/components/article-categories/AddEditForm';

export default {
    page: {
        title: 'Manage Article Category'
    },

    validations: {
        category: {
            name: {
                required
            },
            slug: {
                required
            }
        }
    },

    components: {
        AddEditForm,
        ItemInformation
    },

    data() {
        const { id = null } = this.$route.params;

        return {
            title: id ? 'Edit article category' : 'Add article category',
            items: [
                {
                    text: 'Aguda',
                    to: '/'
                },
                {
                    text: 'Article Categories',
                    to: '/article-categories'
                },
                {
                    text: id ? 'Edit' : 'Add',
                    active: true
                }
            ],
            id,
            editMode: !!id,
            category: {
                meta: {}
            },
            informationKey: 0,
            serverErrors: [],
            categoryValueStringify: ''
        };
    },

    computed: {
        sections() {
            const sections = {
                permalink: true,
                meta: true
            };

            if (this.editMode) {
                sections.status = true;
            }

            return sections;
        },

        previewUrl() {
            return `${frontendUrl}/article-categories/${this.category.slug}`;
        },

        slugError() {
            return this.$getValidationErrorMessageHandler.handle(
                this.$v.category,
                this.serverErrors,
                'slug'
            );
        }
    },

    async created() {
        if (this.editMode) {
            try {
                this.category = await this.getCategory(this.id);
                this.categoryValueStringify = JSON.stringify(this.category);
            } catch (error) {
                console.error(error);

                this.$toasterError();
            }
        }
    },

    methods: {
        ...mapActions({
            addCategory: 'articleCategories/store',
            deleteCategory: 'articleCategories/destroy',
            getCategory: 'articleCategories/show',
            updateCategory: 'articleCategories/update'
        }),

        async submitHandler() {
            this.serverErrors = [];

            this.$v.$touch();

            if (this.$v.$invalid) {
                return;
            }

            try {
                if (!this.editMode) {
                    const added = await this.addCategory(this.category);

                    this.$router.push(`/article-categories/${added.id}/edit`);
                } else {
                    const { updatedAt } = await this.updateCategory({
                        id: this.id,
                        data: this.category
                    });

                    this.category.updatedAt = updatedAt;
                    this.categoryValueStringify = JSON.stringify(this.category);

                    this.$toaster('Category updated!');
                }
            } catch (error) {
                const { response } = error;

                if (response) {
                    const { data, status } = response;

                    if (status === HTTP.BAD_REQUEST && data.errors) {
                        this.serverErrors = data.errors;

                        this.$toasterError('Recheck your form.');

                        return;
                    }
                }

                console.error(error);

                this.$toasterError();
            }
        },

        async onDeleteConfirm() {
            try {
                await this.deleteCategory(this.id);

                this.$router.push('/article-categories');
            } catch (error) {
                const { response } = error;

                if (response) {
                    const { data, status } = response;

                    if (status === HTTP.UNPROCESSABLE_ENTITY && data) {
                        this.$toasterError(data);

                        return;
                    }
                }

                console.error(error);

                this.$toasterError();
            }
        },

        onPreviewButtonClick(e) {
            if (this.categoryValueStringify !== JSON.stringify(this.category)) {
                e.preventDefault();

                this.$toaster('Please save changes before preview!', {
                    title: 'Warning!',
                    variant: 'warning'
                });
            }
        },

        onMetaChange(meta) {
            this.category.meta = { ...meta };
        },

        onNameChange() {
            if (this.editMode) {
                return;
            }

            this.onSlugChange(this.category.name);
            this.informationKey++;
        },

        onSlugChange(slug) {
            this.category.slug = slug;
        }
    }
};
</script>
